import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppConfig } from '../../utils/app-config.service';
import { Id, OptimalSelectedProduct, OrderClient, Product, SelectedProduct } from '../../domain/data';
import {
    IdInterface,
    OptimalSelectedProductInterface,
    OrderClientInterface,
    ProductInterface,
    SelectedProductInterface,
} from '../../../core';
import { LocalStorage } from '../helpers/local-storage';
import { MyHttpClient } from './my-http-client';
import { Serializer } from '../helpers/serializer/serializer';

import { mapCommonResponse } from './api-client.helpers';

import { environment } from '../../../../environments/environment';
import { ProductsbyOrderTypeInterface } from 'src/app/core/domain/data/productsbyordertype.interface';
import { ProductsByOrdertype } from '../../domain/data/productsbyordertype';

@Injectable()
export class ProductApiClient {
    constructor(private http: MyHttpClient, private localStorage: LocalStorage, private serializer: Serializer) {}

    addSelectedProducts(contractCode: IdInterface, products: ProductInterface[]): Observable<SelectedProductInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.add_selected_products).pipe(
            switchMap((url) => {
                const selectedProducts: SelectedProduct[] = [];

                products.map((product) => {
                    const selectedProduct = new SelectedProduct();
                    selectedProduct.productId = product.id as Id;
                    selectedProduct.code = product.code as Id;
                    selectedProduct.encryptedCode = product.encryptedCode as Id;
                    selectedProduct.name = product.name;
                    selectedProduct.isin = product.isin;

                    selectedProducts.push(selectedProduct);
                });

                return this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                        Products: this.serializer.toJsonArray(selectedProducts),
                    })
                    .pipe(map((resp: any) => this.serializer.fromJsonArray(resp, SelectedProduct)));
            })
        );
    }

    checkSelectedProducts(contractCode: IdInterface, products: SelectedProductInterface[]): Observable<SelectedProductInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.check_selected_products).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                        Products: this.serializer.toJsonArray(products),
                    })
                    .pipe(map((resp: any) => this.serializer.fromJsonArray(resp, SelectedProduct)))
            )
        );
    }

    getMyProducts(contractCode: IdInterface): Observable<ProductInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_products).pipe(
            switchMap((url) => {
                let distributionChannel = [
                    'Comercialización agente',
                    'Comercialización agente + Comercialización web',
                    'Comercialización agente + Asesoramiento',
                    'Comercialización agente + Comercialización web + Asesoramiento',
                ];

                if (environment.portalClient) {
                    distributionChannel = [
                        'Comercialización web',
                        'Comercialización agente + Comercialización web',
                        'Comercialización web + Asesoramiento',
                        'Comercialización agente + Comercialización web + Asesoramiento',
                    ];
                }

                return this.http
                    .post(url, {
                        DistributionChannel: distributionChannel,
                        RiskLevel: '',
                        ContractCode: String(contractCode),
                    })
                    .pipe(map((resp: any) => this.serializer.fromJsonArray(resp, Product)));
            })
        );
    }

    getOptimalSelectedProduct(
        contractCode: IdInterface,
        productCode: IdInterface,
        amount: number
    ): Observable<OptimalSelectedProductInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_optimal_selected_product).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                        ProductCode: String(productCode),
                        Amount: amount,
                    })
                    .pipe(map((resp: any) => this.serializer.fromJson(resp, OptimalSelectedProduct)))
            )
        );
    }

    getProduct(productCode: IdInterface): Observable<ProductInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_product).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        Code: String(productCode),
                        Year: 1,
                    })
                    .pipe(
                        map((resp: any) => {
                            if (resp && resp.Id) {
                                return this.serializer.fromJson(resp, Product);
                            }

                            throw new Error('error.general');
                        })
                    )
            )
        );
    }

    getProducts(): Observable<ProductInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_products).pipe(
            switchMap((url) => {
                let distributionChannel = [
                    'Comercialización agente',
                    'Comercialización agente + Comercialización web',
                    'Comercialización agente + Asesoramiento',
                    'Comercialización agente + Comercialización web + Asesoramiento',
                ];

                if (environment.portalClient) {
                    distributionChannel = [
                        'Comercialización web',
                        'Comercialización agente + Comercialización web',
                        'Comercialización web + Asesoramiento',
                        'Comercialización agente + Comercialización web + Asesoramiento',
                    ];
                }

                return this.http
                    .post(url, {
                        DistributionChannel: distributionChannel,
                        RiskLevel: '',
                    })
                    .pipe(map((resp: any) => this.serializer.fromJsonArray(resp, Product)));
            })
        );
    }

    getSelectedProducts(contractCode: IdInterface, clientCode?: IdInterface): Observable<SelectedProductInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_selected_products).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                        ClientCode: clientCode ? String(clientCode) : undefined,
                    })
                    .pipe(map((resp: any) => this.serializer.fromJsonArray(resp, SelectedProduct)))
            )
        );
    }

    getSelectedProductsSignatories(product: SelectedProductInterface, clientCode: IdInterface): Observable<OrderClientInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_selected_products_signatories).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        SelectedProduct: this.serializer.toJson(product),
                        ClientCode: String(clientCode),
                    })
                    .pipe(map((resp: any) => this.serializer.fromJsonArray(resp.OrderClients || [], OrderClient)))
            )
        );
    }

    removeSelectedProduct(product: SelectedProductInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.remove_selected_product).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        Product: this.serializer.toJson(product),
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)))
            )
        );
    }

    updateSelectedProducts(contractCode: IdInterface, products: SelectedProductInterface[]): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.set_selected_products).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                        Products: this.serializer.toJsonArray(products),
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)))
            )
        );
    }

    /* */
    getProductsByOrderType(portfolioCode: IdInterface, clientCode?: IdInterface, distributionChannel?: string[], orderType?: string , riskLevel?: string ): Observable<ProductInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_productsbyordertype).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        PortfolioCode: String(portfolioCode),
                        ClientCode: clientCode ? String(clientCode) : undefined,
                        DistributionChannel: distributionChannel ? distributionChannel : undefined,
                        OrderType: orderType ? String(orderType) : undefined,
                        RiskLevel: riskLevel ? String(riskLevel) : undefined,

                    })
                    .pipe(map((resp: any) => {
                        localStorage.setItem('HiddenOperations', resp.hiddenOperations);
                        return this.serializer.fromJsonArray(resp.Products || [], Product)
                    }))


            )
        );
    }

    getProductsByOrderTypeClient(portfolioCode: IdInterface, clientCode?: IdInterface, distributionChannel?: string[], orderType?: string , riskLevel?: string ): Observable<ProductInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_productsbyordertypeclient).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        PortfolioCode: String(portfolioCode),
                        DistributionChannel: distributionChannel ? distributionChannel : undefined,
                        OrderType: orderType ? String(orderType) : undefined,
                        RiskLevel: riskLevel ? String(riskLevel) : undefined,

                    })
                    .pipe(map((resp: any) => {
                        localStorage.setItem('HiddenOperations', resp.hiddenOperations);
                        return this.serializer.fromJsonArray(resp.Products || [], Product)
                    }))


            )
        );
    }
}
